/**
 * Various DOM helpers.
 */

import { toArray } from './collection';
import { isFunc, isPlainObject, isString, isUndefined } from './js';
import { camelCase } from './string';

// Cached regexes
const REGEX = {
  onlyNumber: /^-?\d+$/,
  // Also make sure the number of periods are exactly one if testing for float
  onlyNumberWithPeriods: /^-?[\d.]+$/,
  validNodeName: /^[\w\d-]+$/,
  leadingData: /^data-?/i,
};

/**
 * Parse an HTML string to DOM nodes.
 *
 * NOTE: Doesn't handle full documents since the <body> can't be set as a
 * child to a regular node. Consider using `DOMParser` for those cases.
 *
 * Extracted and tweaked version of parseHTML from jQuery 2.1.3, without
 * handling of script tags. Changed to return a DocumentFragment instead
 * of an array.
 *
 * Grabbed from https://gist.github.com/Munawwar/6e6362dbdf77c7865a99.
 *
 * A much simpler solution would be using a template element:
 *
 *     function parseHTML(htmlString) {
 *       const tmp = document.createElement('template');
 *       tmp.innerHTML = htmlString.trim();
 *       return tmp.content;
 *     }
 *
 * This will be available when IE11 support is no longer relevant.
 *
 * @param {string} elem - A string containing HTML.
 * @returns {DocumentFragment}
 */
export function parseHTML(elem) {
  const xhtmlTagRegex =
    /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([\w:]+)[^>]*)\/>/gi;
  const tagNameRegex = /<([\w:]+)/;
  const htmlRegex = /<|&#?\w+;/;

  // We have to close these tags to support XHTML (#13200)
  const wrapMap = {
    // Support: IE9
    option: [1, "<select multiple='multiple'>", '</select>'],

    thead: [1, '<table>', '</table>'],
    col: [2, '<table><colgroup>', '</colgroup></table>'],
    tr: [2, '<table><tbody>', '</tbody></table>'],
    td: [3, '<table><tbody><tr>', '</tr></tbody></table>'],

    _default: [0, '', ''],
  };

  let tmp;
  let tag;
  let wrap;
  let j;
  const fragment = document.createDocumentFragment();

  if (!htmlRegex.test(elem)) {
    // Convert non-HTML into a text node
    fragment.append(document.createTextNode(elem));
  } else {
    // Convert HTML into DOM nodes
    tmp = document.createElement('div');
    fragment.append(tmp);

    // Deserialize a standard representation
    tag = (tagNameRegex.exec(elem) || ['', ''])[1].toLowerCase();
    wrap = wrapMap[tag] || wrapMap._default;
    tmp.innerHTML =
      wrap[1] + elem.replaceAll(xhtmlTagRegex, '<$1></$2>') + wrap[2];

    // Descend through wrappers to the right content
    [j] = wrap;
    // Changing `j--` to `j -= 1` changes semantics somehow and leads to a
    // TypeError where tmp is null.
    // eslint-disable-next-line no-plusplus
    while (j--) {
      tmp = tmp.lastChild;
    }

    // Remove wrappers and append created nodes to fragment
    fragment.firstChild.remove();
    while (tmp.firstChild) {
      fragment.append(tmp.firstChild);
    }
  }

  return fragment;
}

/**
 * Set inner HTML and recreate script tags to execute them
 *
 * @param {Element} elem - The Element to set inner HTML on
 * @param {string} html - The HTML string
 */
export function setInnerHTML(elem, html) {
  elem.innerHTML = html;

  Array.from(elem.querySelectorAll('script')).forEach((oldScript) => {
    const newScript = document.createElement('script');
    Array.from(oldScript.attributes).forEach((attr) =>
      newScript.setAttribute(attr.name, attr.value),
    );
    newScript.append(document.createTextNode(oldScript.innerHTML));
    oldScript.parentNode.replaceChild(newScript, oldScript);
  });
}

const NODE_COLLECTION_PROTOTYPES = [
  globalThis.HTMLCollection?.prototype,
  globalThis.HTMLFormControlsCollection?.prototype,
  globalThis.HTMLOptionsCollection?.prototype,
  globalThis.NodeList?.prototype,
].filter(Boolean);

// A subset of the more common elements.
// https://developer.mozilla.org/en-US/docs/Web/SVG/Element
const SVG_ELEMENTS = [
  'circle',
  'clipPath',
  'defs',
  'desc',
  'ellipse',
  'filter',
  'g',
  'line',
  'linearGradient',
  'marker',
  'mask',
  'path',
  'pattern',
  'polygon',
  'polyline',
  'radialGradient',
  'rect',
  'stop',
  'svg',
  'symbol',
  'text',
  'textPath',
  'title',
  'tspan',
  'use',
];
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

/**
 * Create an HTML element, optionally with attributes and/or children.
 *
 * Can be used as a JSX function by declaring pragmas at the top of a file:
 *
 *     /**
 *      * @jsxRuntime classic
 *      * @jsx el
 *      * @jsxFrag el.fragment
 *      * /
 *
 * This will assume `el` is available in scope so be sure to import it.
 *
 * @param {string|Function} tag - HTML tag name or function that returns a node.
 * @param {...object|string|HTMLElement} attrChildren - Optionally include an
 *   object of attributes first, the rest are child nodes. Strings are converted
 *   to text nodes.
 * @returns {HTMLElement|DocumentFragment} - A fragment when using fragments
 *   in JSX (or manually passing el.fragment for the tag).
 * @example
 *
 * // Actual outerHTML results are without line breaks or indentation.
 *
 * el(
 *   'nav',
 *   { 'aria-label': 'Main nav', 'class': 'main-nav' },
 *   el(
 *     'ul',
 *     el('li', { class: 'active' }, 'Item 1'),
 *     el('li', 'Item 2'),
 *   ),
 * ).outerHTML;
 * // =>
 * // '<nav aria-label="Main nav" class="main-nav">
 * //    <ul>
 * //      <li class="active">Item 1</li>
 * //      <li>Item 2</li>
 * //    </ul>
 * // </nav>'
 *
 * // JSX. Note that unlike React, regular attribute names are used rather
 * // than the camelCase JavaScript property names (e.g. use `class` instead
 * // of `className`).
 * const nums = [1, 2];
 * const Heading = ({ children }) => <h2 class="h1">{children}</h2>;
 * (<div>
 *   {nums.length < 3 && <Heading>Few nums</Heading>}
 *   {nums.length >= 3 && <Heading>Many nums</Heading>}
 *   <ul>
 *     {nums.map((num) => (
 *       <li id={`num-${num - 1}`}>{num}</li>
 *     ))}
 *   </ul>
 * </div>).outerHTML;
 * // =>
 * // <div>
 * //   <h2 class="h1">Few nums</h2>
 * //   <ul>
 * //     <li id="num-0">1</li>
 * //     <li id="num-1">2</li>
 * //   </ul>
 * // </div>
 */
export function el(tag, ...attrChildren) {
  /* eslint-disable no-nested-ternary */

  const isFunction = isFunc(tag);
  const isComponent = isFunction && tag !== el.fragment;
  const isSvg = SVG_ELEMENTS.includes(tag);
  const props = Array.from(attrChildren).filter((c) => !!c);
  let attr = {};
  let children = props;
  if (isPlainObject(props[0])) {
    attr = props[0];
    children = props.slice(1);
  }

  // https://stackoverflow.com/questions/8173217/createelement-vs-createelementns
  // prettier-ignore
  const elem =
    isFunction ? tag({ ...attr, children }) :
    isSvg ? document.createElementNS(SVG_NAMESPACE, tag) :
    document.createElement(tag);

  if (isComponent) {
    return elem;
  }

  Object.entries(attr).forEach(([attrName, attrVal]) => {
    // Non-strict equal for null also matches undefined.
    if (attrVal != null && attrVal !== false) {
      elem.setAttribute(attrName, attrVal === true ? '' : attrVal);
    }
  });

  children.forEach((child) => {
    // Handle child being an array
    const childEls = toArray(child).map((c) =>
      isString(c) ? document.createTextNode(c) : c,
    );
    childEls.forEach((c) => {
      elem.append(c);
    });
  });

  return elem;
}
el.fragment = () => document.createDocumentFragment();

/**
 * Check if an object is an `Element` DOM node.
 *
 * @param {*} obj - Element or whatever to check.
 * @param {string|Array.<string>} [nodeName] - Check for specific types of
 *   elements.
 * @param {object} [attrs] - Check for specific attributes, inclusive.
 * @returns {boolean}
 * @example
 *
 * // Check if an object is any element.
 * isElement(obj);
 *
 * // Check if a node is a div.
 * isElement(e.target, 'div');
 *
 * // Check if a node is a span or a div with the `aria-live` attribute set
 * // to `polite` and `id` set to `status` or `updates`.
 * isElement(
 *   node,
 *   ['span', 'div'],
 *   { 'aria-live': 'polite', id: ['status', 'updates'] },
 * );
 */
export function isElement(obj, nodeName = '', attrs = null) {
  // Theoretically vulnerable to any plain objects that has a `nodeType`
  // property set, but that is just doing weird stuff so it shouldn't be
  // a real problem.
  if (typeof obj !== 'object' || obj.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  if (nodeName) {
    const names = toArray(nodeName).map((n) => n.toLowerCase());
    if (!names.includes(obj.nodeName.toLowerCase())) {
      return false;
    }
  }
  if (attrs) {
    return Object.entries(attrs).every(([attr, values]) =>
      toArray(values).some(
        (val) => obj[attr] === val || obj.getAttribute(attr) === val,
      ),
    );
  }
  return true;
}

/**
 * Check if something is a collection of elements.
 *
 * Can either be an array of nodes or a proper collection like `NodeList`.
 *
 * @param {*} elem - Element or whatever to check.
 * @returns {boolean}
 */
export function isElementCollection(elem) {
  return (
    (Array.isArray(elem) && elem.every((e) => isElement(e))) ||
    NODE_COLLECTION_PROTOTYPES.some((proto) =>
      Object.prototype.isPrototypeOf.call(proto, elem),
    )
  );
}

/**
 * Check if a node exists in the DOM rather than just in memory.
 *
 * @param {Element} node - Element to check.
 * @returns {boolean}
 */
export function isInDom(node) {
  const html = document.documentElement;
  return Boolean(node) && (node === html || html.contains(node));
}

/**
 * Get SVG icon HTML that references a name with `xlink:href`.
 *
 * Assumes the existence of an SVG sprite, i.e. a collection of symbol elements
 * with IDs named `icon-[name]`.
 *
 * @param {string} name - Icon name.
 * @returns {string}
 */
export function getSVGIcon(name) {
  return `<span class="icon icon--${name}" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" focusable="false" tabindex="-1"><use xlink:href="#icon-${name}"></use></svg></span>`;
}

/**
 * Convert dataURL to Blob
 *
 * @param {string} dataURL
 * @returns {Blob}
 */
export function dataURLtoBlob(dataURL) {
  const parts = dataURL.split(',');
  const mime = parts[0].match(/:(.*?);/)[1];
  const byteString = atob(parts[1]);
  let n = byteString.length;
  const u8arr = new Uint8Array(n);
  // eslint-disable-next-line no-plusplus
  while (n--) {
    u8arr[n] = byteString.codePointAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

/* ========================================================================= *\
    Attributes
\* ========================================================================= */

/**
 * Get attributes for an inline `svg` element.
 *
 * @param {number} width - Width for both `width` and `viewBox`.
 * @param {number} height - Height for both `height` and `viewBox`.
 * @returns {object}
 */
export function getInlineSVGAttrs(width, height) {
  return {
    'width': width,
    'height': height,
    'viewBox': `0 0 ${width} ${height}`,
    'aria-hidden': 'true',
    'focusable': 'false',
    'tabindex': '-1',
  };
}

/**
 * Get class names.
 *
 * @param {...string|object} classes - Classes as is or objects where the key
 *   is the class name to and the value's truthyness decides if it's included.
 * @returns {?string}
 * @example
 *
 * const isActive = true;
 * const isDisabled = false;
 *
 * classNames('foo', 'bar', {
 *   'foo--active': isActive,
 *   'foo--disabled': isDisabled,
 * });
 * // => 'foo bar foo--active'
 *
 * classNames({ 'foo--disabled': isDisabled });
 * // => null
 */
export function classNames(...classes) {
  let className = '';
  Array.from(classes)
    .filter((c) => !!c)
    .forEach((part) => {
      if (isString(part)) {
        className += ` ${part.trim()}`;
      } else {
        Object.entries(part).forEach(([cls, isIncluded]) => {
          if (isIncluded) {
            className += ` ${cls.trim()}`;
          }
        });
      }
    });
  return className.trim() || null;
}

const DATA_VALUE_MAP = {
  true: true,
  false: false,
  null: null,
  NaN,
};

/**
 * Decode an attribute value.
 *
 * @param {*} rawVal - The value to parse.
 * @param {Function} [valueParser] - Custom parser for the value, it will get
 *   the raw string value as a parameter and should return the desired result.
 * @returns {*} Raw or parsed value.
 * @example
 *
 * // Most numbers are automatically parsed. Use the Number constructor as a
 * // parser to convert the value to a number when that's not the case.
 * decodeAttr('00123', Number);
 * // => 123
 *
 * // Some string values like 'false' are parsed to avoid something like
 * // Boolean('false') -> true.
 * decodeAttr('false');
 * // => false
 *
 * // Reading an object that used JSON.stringify as encoder.
 * decodeAttr('{"this":"is","complex":33}', JSON.parse);
 * // => { this: 'is', complex: 33 }
 */
export function decodeAttr(rawVal, valueParser = null) {
  // Handle empty or undefined boolean values
  if (valueParser === Boolean) {
    if (rawVal === '') {
      return true;
    }
    if (rawVal === undefined || rawVal === 'undefined') {
      return false;
    }
  }

  // Empty values are represented with null
  if (rawVal === '' || rawVal === undefined || rawVal === 'undefined') {
    return null;
  }

  // Handle stringified data types like 'false' and 'null'
  if (!isUndefined(DATA_VALUE_MAP[rawVal])) {
    return DATA_VALUE_MAP[rawVal];
  }

  // If a custom parser is passed, let it do the work
  if (valueParser) {
    return valueParser(rawVal);
  }

  // Automatically parse numbers
  if (
    // Abort if starting with zero, to avoid converting something like
    // 0012 to 12.
    (rawVal === '0' || rawVal[0] !== '0') &&
    // Only digits...
    (REGEX.onlyNumber.test(rawVal) ||
      // ...or digits and a single period
      (REGEX.onlyNumberWithPeriods.test(rawVal) &&
        rawVal.match(/\./g).length === 1))
  ) {
    return Number(rawVal);
  }

  return rawVal;
}

/**
 * Encode an attribute value.
 *
 * @param {*} val - Value to encode.
 * @param {Function} [valueEncoder] - Custom encoder for the value, it will get
 *   the raw value as a parameter and should the encoded result.
 * @returns {string}
 * @example
 *
 * encodeAttr(3);
 * // => '3'
 *
 * // A boolean true results in an empty value.
 * encodeAttr(true);
 * // => ''
 *
 * encodeAttr({ this: 'is', complex: 33 }, JSON.stringify);
 * // => '{"this":"is","complex":33}'
 */
export function encodeAttr(val, valueEncoder = null) {
  return valueEncoder ? valueEncoder(val) : String(val);
}

/**
 * Format a key used in data attribute helpers.
 *
 * Strips leading `data-` and converts to camelCase to allow both the HTML and
 * DOM name formats.
 *
 * @param {string} key - Raw key name.
 * @returns {string}
 */
function _getDataKey(key) {
  return camelCase(key.replace(REGEX.leadingData, ''));
}

/**
 * Save data on a DOM element data attribute.
 *
 * @param {Element} elem - Element to save data on.
 * @param {string} key - Data key, will result in the attribute `data-[key]`.
 *   Note that the key can be camelCase, but the resulting attribute will
 *   always display as kebab-case in the DOM tree.
 * @param {*} [val] - Value to save, defaults to empty string. Will be
 *   converted to a string as-is by default, so false and null will for
 *   example be converted to 'false' and 'null' respectively.
 * @param {Function} [valueEncoder] - Value encoder. Will get the raw value
 *   as a parameter and should return a string. This should only be required
 *   for custom serialization of complex types like objects that can't be
 *   converted to a useful string automatically.
 * @example
 *
 * setData(elem, 'count', 3);
 * // => elem.dataset.count === '3'
 *
 * setData(elem, 'count', null);
 * // => elem.dataset.count === 'null'
 *
 * // Equivalent of setting `<elem data-is-active></elem>` in the HTML.
 * setData(elem, 'is-active');
 *
 * // Save an object. Not using `JSON.stringify` as the encoder would result
 * // in '[object Object]'.
 * setData(elem, 'someData', { this: 'is', complex: 33 }, JSON.stringify);
 */
export function setData(elem, key, val = '', valueEncoder = null) {
  elem.dataset[_getDataKey(key)] = encodeAttr(val, valueEncoder);
}

/**
 * Read data from a DOM element data attribute.
 *
 * @param {Element} elem - Element to read data from.
 * @param {string} key - Data key. Can be kebab-case or the camelCase
 *   equivalent of it; `data-test` requires 'test' as the key while
 *   `data-other-test` can be 'other-test' or 'otherTest'.
 * @param {Function} [valueParser] - Custom parser for the value, it will get
 *   the raw string value as a parameter and should return the desired result.
 * @returns {*} Raw or parsed value.
 * @example
 *
 * // Most numbers are automatically parsed. Use the Number constructor as a
 * // parser to convert the value to a number when that's not the case.
 * `<div data-count="3" data-serial-number="00123"></div>`
 * getData(div, 'count');
 * // => 3
 * getData(div, 'serial-number', Number);
 * // => '00123'
 * getData(div, 'serial-number', Number);
 * // => 123
 *
 * // Keys in kebab-case are handled, and some string values like 'false' are
 * // parsed to avoid something like Boolean('false') -> true. Use the Boolean
 * // constructor as a parser for boolean attributes.
 * `<div data-is-active="false" data-loading></div>`
 * getData(elem, 'is-active');
 * // => false
 * getData(elem, 'loading');
 * // => null
 * getData(elem, 'loading', Boolean);
 * // => true
 *
 * // Reading an object that used JSON.stringify as encoder
 * getData(elem, 'someData', JSON.parse);
 * // => { this: 'is', complex: 33 }
 */
export function getData(elem, key, valueParser = null) {
  return decodeAttr(elem.dataset[_getDataKey(key)], valueParser);
}

/**
 * Remove a DOM element data attribute.
 *
 * @param {Element} elem - Element to read data from.
 * @param {string} key - Data key. Can be kebab-case or the camelCase
 *   equivalent of it; `data-test` will use 'test' as the key while
 *   `data-other-test` can be 'other-test' or 'otherTest'.
 */
export function removeData(elem, key) {
  delete elem.dataset[_getDataKey(key)];
}

/* ========================================================================= *\
    Element selection
\* ========================================================================= */

const NODE_NAME_ATTRIBUTE = 'data-node-name';

/**
 * Check if a value is a valid `data-node-name`.
 *
 * @param {string} value
 * @returns {boolean}
 */
function _isValidNodeName(value) {
  // Values are cast to strings so default any falsy values.
  return REGEX.validNodeName.test(value || '');
}

/**
 * Get a `data-node-name` selector.
 *
 * @param {string} selector - Raw selector.
 * @returns {string}
 */
function _getNodeNameSelector(selector) {
  return `[${NODE_NAME_ATTRIBUTE}~="${selector}"]`;
}

/**
 * Get an element's node-name data attribute value.
 *
 * @param {Element} elememnt - Element to get name from.
 * @returns {?string} Name, or null if no data-node-name has been set.
 */
export function getNodeName(elememnt) {
  return getData(elememnt, NODE_NAME_ATTRIBUTE);
}

/**
 * Check if an element has the specified node-name data attribute value.
 *
 * @param {Element} elememnt - Element to check.
 * @param {string} nodeName - Node name to check for.
 * @returns {boolean}
 */
export function hasNodeName(elememnt, nodeName) {
  return getNodeName(elememnt) === nodeName;
}

/**
 * Get DOM nodes that match a selector.
 *
 * @ignore
 * @param {Element} parent - Element to select from, the result will be
 *   decendants of this.
 * @param {string} selector - Element selector.
 * @returns {Array.<HTMLElement>} An array of found elements, can be empty.
 */
function _selectAll(parent, selector) {
  // Values are cast to strings so default any falsy values to an invalid
  // selector to trigger an error.
  return Array.from(parent.querySelectorAll(selector || ''));
}

/**
 * Get a single DOM node that matches a selector.
 *
 * @ignore
 * @param {Element} parent - Element to select from, the result will be a
 *   decendant of this.
 * @param {string} selector - Element selector.
 * @returns {?HTMLElement} Found element or null.
 */
function _selectSingle(parent, selector) {
  // Values are cast to strings so default any falsy values to an invalid
  // selector to trigger an error.
  return parent.querySelector(selector || '') || null;
}

/**
 * Select DOM nodes.
 *
 * @ignore
 * @param {Element|string} parentOrSelector - Element to select from or a
 *   selector to select from the document.
 * @param {string} selector - Node selector if passing a parent.
 * @param {Function} selectionFunc - Selection function.
 * @returns {Array.<Element> | ?Element} An array of found elements or a
 *   single found element, can be empty or null.
 */
function _getEl(parentOrSelector, selector, selectionFunc) {
  const isdefaultParent = isString(parentOrSelector);
  const parent = isdefaultParent ? document : parentOrSelector;
  const actualSelector = isdefaultParent ? parentOrSelector : selector;
  // Selector is a valid data-node-name, assume that's the intended use
  // rather than an HTML element for example.
  if (_isValidNodeName(actualSelector)) {
    const result = selectionFunc(parent, _getNodeNameSelector(actualSelector));
    const hasResult = Array.isArray(result) ? result.length : result;
    // Use default selection below as a fallback.
    if (hasResult) {
      return result;
    }
  }
  // Default to using the selector as is.
  return selectionFunc(parent, actualSelector);
}

/**
 * Get all nodes matching the selector inside the specified node.
 *
 * @param {Element|string} parentOrSelector - Element to select from or a
 *   selector to select from the document.
 * @param {string} [selector] - Selector if a parent is passed as the first
 *   argument.
 * @returns {Array.<HTMLElement>} An array of found elements, can be empty.
 * @example
 *
 * // Select all p elements in the document.
 * selectAll('p');
 *
 * // Select all li elements inside the menu node.
 * selectAll(menuNode, 'li');
 */
export function selectAll(parentOrSelector, selector) {
  return _getEl(parentOrSelector, selector, _selectAll);
}

/**
 * Get the first node matching the selector inside the specified node.
 *
 * @param {Element|string} parentOrSelector - Element to select from or a
 *   selector to select from the document.
 * @param {string} [selector] - Selector if a parent is passed as the first
 *   argument.
 * @returns {?HTMLElement} Found element or null.
 * @example
 *
 * // Select the first header element in the document.
 * selectSingle('header');
 *
 * // Select the first h2 element inside the site footer node.
 * selectSingle(siteFooter, 'h2');
 */
export function selectSingle(parentOrSelector, selector) {
  return _getEl(parentOrSelector, selector, _selectSingle);
}

/**
 * Get all direct children of an element matching the selector.
 *
 * @param {Element} parent - Element to select from.
 * @param {string} selector - Element selector.
 * @returns {Array.<HTMLElement>} An array of matching children, can be empty.
 * @example
 *
 * // <ul id="outer">
 * //   <li>
 * //     Outer 1
 * //     <ul id="nested">
 * //       <li>Nested 1</li>
 * //       <li>Nested 2</li>
 * //     </ul>
 * //   </li>
 * //   <li>Outer 2</li>
 * // </ul>
 * selectChildren(document.getElementById('outer'), 'li');
 * // => [<li>Outer 1</li>, <li>Outer 2</li>]
 */
export function selectChildren(parent, selector) {
  return Array.from(parent.children).filter(
    (child) =>
      child.matches(_getNodeNameSelector(selector)) || child.matches(selector),
  );
}

/**
 * Get the first direct child of an element matching the selector.
 *
 * @param {Element} parent - Element to select from.
 * @param {string} selector - Element selector.
 * @returns {?HTMLElement} Matching element or null.
 * @example
 *
 * // <section id="section">
 * //   <div class="outer">
 * //     Outer content
 * //     <div class="inner">Inner content</div>
 * //   </div>
 * // </section>
 * selectChildren(document.getElementById('section'), 'div');
 * // => div.outer
 */
export function selectChild(parent, selector) {
  return selectChildren(parent, selector)[0] || null;
}

/* ========================================================================= *\
    Node insertion
\* ========================================================================= */

/**
 * Insert a new element as a sibling before a target.
 *
 * @param {Element} target - Node to insert the new element before.
 * @param {Element} newElement - Element to insert.
 * @example
 *
 * insertBefore(formNode, parseHTML(`<p class="error">${errorMessage}</p>`));
 */
export function insertBefore(target, newElement) {
  target.parentNode.insertBefore(newElement, target);
}

/**
 * Insert a new element as a sibling after a target.
 *
 * @param {Element} target - Node to insert the new element after.
 * @param {Element} newElement - Element to insert.
 * @example
 *
 * insertAfter(list.lastChild, parseHTML(newListItems));
 */
export function insertAfter(target, newElement) {
  target.parentNode.insertBefore(newElement, target.nextSibling);
}

/* ========================================================================= *\
    Dimensions and position
\* ========================================================================= */

/**
 * Get the window's width or height.
 *
 * @param {string} name - `Width` or `Height`, NOTE CAPITAL FIRST LETTER.
 * @returns {number}
 */
function getWindowDimension(name) {
  // https://github.com/jquery/jquery/blob/main/src/dimensions.js
  // Behave like jQuery's outerWidth and include scroll bars
  // https://github.com/jquery/jquery/issues/1729
  return document.documentElement[`client${name}`];
}

/**
 * Get the current window width.
 *
 * @returns {number}
 */
export function getWindowWidth() {
  return getWindowDimension('Width');
}

/**
 * Get the current window height.
 *
 * @returns {number}
 */
export function getWindowHeight() {
  return getWindowDimension('Height');
}

/**
 * Get the document's width or height.
 *
 * @param {string} name - `Width` or `Height`, NOTE CAPITAL FIRST LETTER.
 * @returns {number}
 */
function getDocumentDimension(name) {
  const doc = document;

  // https://github.com/jquery/jquery/blob/main/src/dimensions.js
  return Math.max(
    doc.body[`scroll${name}`],
    doc.documentElement[`scroll${name}`],
    doc.body[`offset${name}`],
    doc.documentElement[`offset${name}`],
    doc.documentElement[`client${name}`],
  );
}

/**
 * Get the current document width.
 *
 * @returns {number}
 */
export function getDocumentWidth() {
  return getDocumentDimension('Width');
}

/**
 * Get the current document height.
 *
 * @returns {number}
 */
export function getDocumentHeight() {
  return getDocumentDimension('Height');
}

/**
 * Get the current window scroll position (distance from the top).
 *
 * @returns {number}
 */
export function getScrollPosition() {
  // Apparently much more performant than window.pageYOffset in some cases.
  // https://github.com/w3c/respec/pull/1685
  return document.documentElement.scrollTop;
}

/**
 * Get the top position of an element relative to the whole document.
 *
 * @param {Element} elem - Element to read.
 * @returns {number}
 */
export function getElementTop(elem) {
  const rect = elem.getBoundingClientRect();
  const clientTop =
    document.documentElement.clientTop || document.body.clientTop || 0;

  return rect.top + window.pageYOffset - clientTop;
}

/**
 * Scroll to the top of an element if its top edge is further up than the
 * current scroll position.
 *
 * @param {Element} elem - Element to scroll to.
 * @param {number} [offset] - Optional offset.
 */
export function scrollToTopIfNeeded(elem, offset = 0) {
  const elemTop = getElementTop(elem);
  const scrollTop = window.pageYOffset + offset;
  if (elemTop < scrollTop) {
    window.scrollTo(0, elemTop + offset);
  }
}

/* ========================================================================= *\
    Events
\* ========================================================================= */

export const GLOBAL_EVENT_TARGET = globalThis;

const READY_LOAD_EVENT_OPTIONS = {
  capture: true,
  once: true,
  // Probably doesn't do anything in this context but it shouldn't hurt.
  passive: true,
};

/**
 * Run a function when the document is ready. Runs right away if already ready.
 *
 * @param {Function} cb - Callback to run.
 */
export function onReady(cb) {
  if (
    document.readyState === 'interactive' ||
    document.readyState === 'complete'
  ) {
    cb();
  } else {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        cb();
      },
      READY_LOAD_EVENT_OPTIONS,
    );
  }
}

/**
 * Run a function when all resources are loaded, or right away if already
 * loaded.
 *
 * @param {Function} cb - Callback to run.
 */
export function onLoad(cb) {
  if (document.readyState === 'complete') {
    cb();
  } else {
    window.addEventListener(
      'load',
      () => {
        cb();
      },
      READY_LOAD_EVENT_OPTIONS,
    );
  }
}

/**
 * Detect dragging on an element.
 *
 * Uses pointer events which notably only got Safari support in version 13,
 * be wary of using for critical functionality.
 *
 * @param {Element} element - Element to bind events on.
 * @param {Function} cb - Callback to run. Is passed the `pointerdown` event
 *   as the first parameter and the `pointerup` event as the second.
 * @param {number} [threshold] - Diff in number of pixels to require in either
 *   the X or Y direction for the callback to be called.
 */
export function onDrag(element, cb, threshold = 20) {
  const eventOpt = { passive: true };
  const down = { x: null, y: null };
  let downEvent = null;
  element.addEventListener(
    'pointerdown',
    (e) => {
      downEvent = e;
      down.x = e.pageX;
      down.y = e.pageY;
    },
    eventOpt,
  );
  element.addEventListener(
    'pointerup',
    (e) => {
      if (
        down.x !== null &&
        down.y !== null &&
        (Math.abs(down.x - e.pageX) >= threshold ||
          Math.abs(down.y - e.pageY) >= threshold)
      ) {
        cb(downEvent, e);
      }
      down.x = null;
      down.y = null;
      downEvent = null;
    },
    eventOpt,
  );
}

/**
 * Trigger a custom event.
 *
 * @param {Element} target - DOM node to trigger the event on.
 * @param {string} eventName - Name of the event to trigger.
 * @param {...*} [extraData] - Any extra data to pass to the listener.
 * @example
 *
 * triggerCustomEvent(document, 'modalOpen', modalData);
 */
export function triggerCustomEvent(target, eventName, ...extraData) {
  target.dispatchEvent(
    new CustomEvent(eventName, { detail: extraData, bubbles: true }),
  );
}

/**
 * Trigger a custom event that can be canceled
 *
 * @param {Element} target - DOM node to trigger the event on.
 * @param {string} eventName - Name of the event to trigger.
 * @param {...*} [extraData] - Any extra data to pass to the listener.
 * @returns {boolean} True if the event's preventDefault has not been invoked,
 *   otherwise false.
 * @example
 *
 * const isCancelled =
 *   !triggerCancelableCustomEvent(document, 'modalOpen', modalData);
 */
export function triggerCancelableCustomEvent(target, eventName, ...extraData) {
  return target.dispatchEvent(
    new CustomEvent(eventName, {
      detail: extraData,
      bubbles: true,
      cancelable: true,
    }),
  );
}

/**
 * Trigger a global custom event.
 *
 * @param {string} eventName - Name of the event to trigger.
 * @param {...*} [extraData] - Any extra data to pass to the listener.
 * @example
 *
 * triggerGlobalCustomEvent('modalOpen', modalData);
 */
export function triggerGlobalCustomEvent(eventName, ...extraData) {
  triggerCustomEvent(GLOBAL_EVENT_TARGET, eventName, ...extraData);
}
